import type { CompanyShellRow } from "../../generated/core-api";
import type {
  CompanyShellCsvColumn,
  CompanyShellCsvParseError,
} from "../types";
import {
  CSV_HEADER_TO_FIELD_PATH,
  isBlockingCompanyShellParseError,
  unknownShellCsvColumnCellKey,
} from "./parseCompanyShellCsv";
import {
  cellErrorKey,
  type MappedValidationErrors,
} from "./mapValidationErrorsToCells";
import {
  collectCompanyShellLiveRowIssues,
  isDuplicateCompanyCodeParseError,
  isRequiredCompanyShellParseError,
} from "./collectCompanyShellLiveRowIssues";

export interface MappedCompanyShellClientIssues
  extends MappedValidationErrors {
  /** Blank, unconditionally-required fields that must be fixed before Validate. */
  requiredIssueCount: number;
  /** Later rows whose Company Code duplicates an earlier row. */
  duplicateCompanyCodeIssueCount: number;
}

function append(
  map: Map<string, string[]>,
  key: string,
  message: string
): void {
  const existing = map.get(key);
  if (existing) existing.push(message);
  else map.set(key, [message]);
}

function appendRow(
  map: Map<number, string[]>,
  key: number,
  message: string
): void {
  const existing = map.get(key);
  if (existing) existing.push(message);
  else map.set(key, [message]);
}

function fieldPath(header: string): string {
  return Object.prototype.hasOwnProperty.call(
    CSV_HEADER_TO_FIELD_PATH,
    header
  )
    ? CSV_HEADER_TO_FIELD_PATH[header]
    : unknownShellCsvColumnCellKey(header);
}

/**
 * Build the active client-side Review issues from the current edited rows.
 *
 * Required-field and duplicate-code issues are recomputed instead of copied
 * verbatim from the upload result, so they disappear immediately when fixed
 * and are added when an edit introduces a new issue. Other parse-time issues
 * are preserved and mapped through their source CSV line where possible.
 */
export function mapCompanyShellClientIssues(
  rows: CompanyShellRow[],
  columns: CompanyShellCsvColumn[],
  parseErrors: CompanyShellCsvParseError[],
  rowCsvLineNumbers: number[]
): MappedCompanyShellClientIssues {
  const cellErrors = new Map<string, string[]>();
  const rowErrors = new Map<number, string[]>();
  const errorsByRow = new Map<number, string[]>();
  const batchErrors: string[] = [];
  let requiredIssueCount = 0;
  let duplicateCompanyCodeIssueCount = 0;

  const appendCellIssue = (
    rowIndex: number,
    header: string,
    message: string
  ) => {
    append(cellErrors, cellErrorKey(rowIndex, fieldPath(header)), message);
    appendRow(errorsByRow, rowIndex, message);
  };

  for (const issue of collectCompanyShellLiveRowIssues(
    rows,
    columns,
    rowCsvLineNumbers
  )) {
    appendCellIssue(
      issue.rowIndex,
      issue.column,
      issue.message
    );
    if (issue.category === "required") requiredIssueCount++;
    if (issue.category === "duplicate_company_code") {
      duplicateCompanyCodeIssueCount++;
    }
  }

  const rowIndexByCsvLine = new Map<number, number>();
  rowCsvLineNumbers.forEach((csvLineNumber, rowIndex) => {
    rowIndexByCsvLine.set(csvLineNumber, rowIndex);
  });

  for (const error of parseErrors) {
    if (
      isRequiredCompanyShellParseError(error) ||
      isDuplicateCompanyCodeParseError(error)
    ) {
      continue;
    }
    // The short row itself needs no further action after its absent trailing
    // values become editable blanks. Any required blanks are recomputed above
    // and remain actionable until the user completes them.
    if (error.code === "missing_trailing_columns") {
      continue;
    }
    if (
      isBlockingCompanyShellParseError(error) ||
      error.csv_line_number === undefined ||
      !error.column
    ) {
      batchErrors.push(error.message);
      continue;
    }

    const rowIndex = rowIndexByCsvLine.get(error.csv_line_number);
    if (rowIndex === undefined) {
      batchErrors.push(error.message);
      continue;
    }
    appendCellIssue(rowIndex, error.column, error.message);
  }

  return {
    cellErrors,
    rowErrors,
    errorsByRow,
    batchErrors,
    requiredIssueCount,
    duplicateCompanyCodeIssueCount,
  };
}
